// React
import { FunctionComponent, useState } from "react";
// Component
import { StatusSelect, Title } from '@fyrstain/fhir-front-library';
// React Bootstrap
import { Card, Form } from 'react-bootstrap';
// Translation
import i18n from 'i18next';

//////////////////////////////////
//          Interface           //
//////////////////////////////////

interface GeneralInformation {
    disableId: boolean,
    id: string,
    automatable: boolean,
    url: string,
    version: string,
    name: string,
    title: string,
    status: string,
    date: string,
    description: string,
    setChanges: (name: string, value: string) => void,
    isValidated: boolean
}

//////////////////////////////////
//             Type             //
//////////////////////////////////

// Links the TestScript status to a display type for the dropdown
type StatusMenu = {
    status: "success" | "suspended" | "errorStatus" | "advisory" | "info" | "unknown";
    statusMessage: string;
};

const GeneralInformationSection: FunctionComponent<GeneralInformation> = (configs: GeneralInformation) => {

    //////////////////////////////////
    //            States            //
    //////////////////////////////////

    // Error messages for General information fields
    const [messages, setMessages] = useState<{ [key: string]: string }>({
        status: i18n.t('errormessage.statusrequired'),
        id: i18n.t('errormessage.requiredfield'),
        title: i18n.t('errormessage.requiredfield'),
        url: i18n.t('errormessage.requiredfield'),
        version: i18n.t('errormessage.requiredfield'),
        description: i18n.t('errormessage.requiredfield')
    });

    // Check if the URL field has been touched
    const [isUrlTouched, setIsUrlTouched] = useState<boolean>(false);
    // Check if the ID field has been touched
    const [isIdTouched, setIsIdTouched] = useState<boolean>(false);

    //////////////////////////////////
    //           Constants          //
    //////////////////////////////////

    // Default selectedOption for the status dropdown.
    // TODO Check that this is a valid option ?
    const defaultSelectStatusOption = !configs.status ?
        i18n.t('placeholder.choosestatus') :
        configs.status;

    //////////////////////////////////
    //            Options           //
    //////////////////////////////////

    // Options for the status select 
    // TODO Use a valueSet
    const statusOptions: StatusMenu[] = [
        { status: 'advisory', statusMessage: 'draft' },
        { status: 'success', statusMessage: 'active' },
        { status: 'info', statusMessage: 'retired' },
        { status: 'unknown', statusMessage: 'unknown' }
    ];

    //////////////////////////////////
    //            Handlers          //
    //////////////////////////////////

    /**
     * Handle the change of the input fields for general informations
     * 
     * @param e the change event.
     * 
     */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const validity = e.target.validity;
        configs.setChanges(name, value);
        if (name === 'url') {
            setIsUrlTouched(true);
        }
        if (validity.valueMissing) {
            setMessages({ ...messages, [name]: i18n.t('errormessage.requiredfield') });
        } else if (name === 'url' && validity.patternMismatch) {
            setMessages({ ...messages, url: i18n.t('errormessage.invalidurlcharacters') });
        } else {
            let newMessages = { ...messages };
            delete newMessages[name];
            setMessages(newMessages);
        }
        if (name === 'id') {
            setIsIdTouched(true);
        }
        if (validity.patternMismatch && isIdTouched) {
            setMessages({ ...messages, id: i18n.t('errormessage.invalidformatid') });
        }
        if (name === 'title') {
            if (!validity.tooLong) {
                const generatedName = value
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join('')
                    .replace(/[^A-Za-z0-9_]/g, '');
                configs.setChanges("name", generatedName);
            }
        }
    };

    /**
     * Handle the change of the status message
     * 
     * @param statusMessage the status message that is selected.
     * 
     */
    const handleStatusChange = (statusMessage: string) => {
        if (statusMessage !== i18n.t('placeholder.choosestatus')) {
            setMessages({ ...messages, status: i18n.t('errormessage.statusrequired') });
        }
        configs.setChanges('status', statusMessage);
        let newMessages = { ...messages };
        delete newMessages.status;
        setMessages(newMessages);
    }

    /**
     * Handle the change of the switch button
     * 
     * @param e the switch event
     * 
     */
    const handleAutomatableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        configs.setChanges(name, checked ? 'automatable' : 'not-automatable');
    };

    //////////////////////////////////
    //          Content             //
    //////////////////////////////////

    return <Card>
        <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex flex-wrap gap-4">
                    <Title
                        level={2}
                        content={i18n.t('title.generalinformations')}
                    />
                    <div className="d-flex justify-content-between gap-2 align-items-center">
                        <StatusSelect
                            defaultSelectOption={defaultSelectStatusOption}
                            statusMessageArray={statusOptions}
                            onChange={handleStatusChange}
                            language={i18n.t}
                            updateTypeTranslation={''}
                        />
                        {configs.isValidated && (
                            <div className="text-danger">
                                {messages.status}
                            </div>
                        )}
                    </div>
                </div>
                <Form.Check
                    className="fw-bold"
                    type="switch"
                    label={i18n.t('label.automatable')}
                    name="automatable"
                    checked={configs.automatable}
                    onChange={handleAutomatableChange}
                />
            </div>
        </Card.Header>

        <Card.Body>
            <div className="d-flex flex-wrap gap-4">
                <Form.Group className="md-flex-container">
                    <Form.Label className="fw-bold">
                        ID *
                    </Form.Label>
                    <Form.Control
                        name="id"
                        required
                        disabled={configs.disableId}
                        value={configs.id}
                        onChange={handleChange}
                        pattern="^[A-Za-z0-9\-\.]{1,64}"
                        isInvalid={isIdTouched && !!messages.id}
                    />
                    <Form.Control.Feedback type="invalid">
                        {messages.id}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="md-flex-container">
                    <Form.Label className="fw-bold">
                        {i18n.t('label.title')} *
                    </Form.Label>
                    <Form.Control
                        required
                        name="title"
                        maxLength={254}
                        value={configs.title}
                        onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                        {messages.title}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="md-flex-container">
                    <Form.Label className="fw-bold">
                        URL *
                    </Form.Label>
                    <Form.Control
                        required
                        name="url"
                        pattern="[^#\|]*"
                        value={configs.url}
                        onChange={handleChange}
                        isInvalid={isUrlTouched && !!messages.url}
                    />
                    <Form.Control.Feedback type="invalid">
                        {messages.url}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="md-flex-container">
                    <Form.Label className="fw-bold">
                        Version *
                    </Form.Label>
                    <Form.Control
                        required
                        name="version"
                        value={configs.version}
                        onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                        {messages.version}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="w-100">
                    <Form.Label className="fw-bold">
                        Description *
                    </Form.Label>
                    <Form.Control
                        required={true}
                        as="textarea"
                        rows={5}
                        name="description"
                        value={configs.description}
                        onChange={handleChange}
                        className="form-control"
                    />
                    <Form.Control.Feedback type="invalid">
                        {messages.description}
                    </Form.Control.Feedback>
                </Form.Group>
            </div>
        </Card.Body>
    </Card>
};

export default GeneralInformationSection;